/**
 * DeleteEventConfirmSheet (Plan 03-08).
 *
 * Destructive confirmation sheet per UI-SPEC §Destructive confirmations.
 * 4 copy variants based on whether the event is recurring and what scope is selected:
 *   - oneoff: one-off (non-recurring) event deletion
 *   - single: delete just this occurrence of a recurring series
 *   - from_this: delete from this occurrence onwards
 *   - series: delete the whole series
 *
 * Per D-08: Slett button is only rendered when caller has verified permission.
 * T-CAL-37: API enforces access independently of UI gate.
 *
 * Utseende (#157, steg 7b): spørsmålet er arkets tittel ved siden av X, og
 * knappene er Button-primitiven (danger og secondary, 52 px).
 */
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { Button } from '../../components/primitives';
import type { Scope } from './RecurrenceOverrideScopePicker';

type DeleteVariant = 'oneoff' | Scope;

const VARIANT_KEY_MAP: Record<DeleteVariant, string> = {
  oneoff: 'oneoff',
  single: 'single',
  from_this: 'fromThis',
  series: 'series',
};

export interface DeleteEventConfirmSheetProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (scope: DeleteVariant) => void;
  title: string;
  isRecurring: boolean;
  scope: Scope;
}

export function DeleteEventConfirmSheet({
  open,
  onClose,
  onConfirm,
  title,
  isRecurring,
  scope,
}: DeleteEventConfirmSheetProps) {
  const { t } = useTranslation();

  const variant: DeleteVariant = !isRecurring ? 'oneoff' : scope;
  const keyPrefix = `calendar.delete.${VARIANT_KEY_MAP[variant]}`;

  const handleConfirm = () => {
    onConfirm(variant);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t(`${keyPrefix}.title`, { title })}
    >
      <div data-testid="delete-event-confirm-sheet" className="flex flex-col gap-4">
        <p className="m-0 text-body text-ink-2">
          {t(`${keyPrefix}.body`)}
        </p>

        <div className="flex flex-col gap-3 pt-2">
          {/* Destructive confirm */}
          <Button
            variant="danger"
            size={52}
            onClick={handleConfirm}
            data-testid="delete-confirm-btn"
            className="w-full"
          >
            {t(`${keyPrefix}.confirm`)}
          </Button>

          {/* Cancel */}
          <Button
            variant="secondary"
            size={52}
            onClick={onClose}
            data-testid="delete-cancel-btn"
            className="w-full"
          >
            {t('calendar.delete.cancel')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
